import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { AsyncPipe } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  CellContextMenuEvent,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
// Services
import { SharedService } from '../../shared/services/shared.service';
import { ContextMenuService } from '../../shared/services/context-menu.service';
// Third Party
import { Subscription } from 'rxjs';

// components
import { EditBtnComponent } from 'src/app/shared/components/ag-grid/edit-btn/edit-btn.component';
import { ExpenseTableColumnDef } from './columnDefs';
import { Expense } from 'src/app/shared/models/accounting';
import { AccountingService } from '../services/accounting.service';
import {
  ComponentNames,
  ModalID,
  StatusModalIndicator,
} from 'src/app/shared/models/constants';
import { AgGridService } from 'src/app/shared/services/ag-grid.service';
import { isObjectEmpty } from 'src/app/shared/functions';

@Component({
  selector: 'app-expense-table',
  templateUrl: './expense-table.component.html',
  styleUrls: ['./expense-table.component.scss'],
  standalone: true,
  imports: [AgGridAngular, AsyncPipe, BsDropdownModule],
})
export class ExpenseTableComponent {
  @ViewChild('grid', { read: ElementRef }) gridElement!: ElementRef;
  @ViewChild('grid') grid!: AgGridAngular; // Keep for grid API access
  subscriptions = new Subscription();

  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };
  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs: ColDef[] = ExpenseTableColumnDef;
  defaultColDef;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    suppressDragLeaveHidesColumns: true,
  };
  components: any;
  theme = agGridDarkTheme;

  // Context Menu state from service
  menuState$ = this.contextMenuService.menuState$;

  constructor(
    public sharedService: SharedService,
    private contextMenuService: ContextMenuService,
    public accountingService: AccountingService,
    private agGridService: AgGridService,
    private cdr: ChangeDetectorRef,
  ) {
    this.gridOptions = <GridOptions>{};
    this.gridOptions.headerHeight = 50;
    this.gridOptions.rowHeight = 28;
    this.gridOptions.enableCellTextSelection = true; // Allows text selection for copying
    this.gridOptions.ensureDomOrder = true; // Ensures proper DOM order for accessibility
    this.gridOptions.suppressClipboardPaste = false; // Allow clipboard operations
    this.gridOptions.context = { ConfigComponent: this }; // this allows access to component functions
    this.gridOptions.rowClass = 'ag-grid-row';
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
    this.components = { ViewEditBtns: EditBtnComponent };
    // expense_records_list_retrieved$
    this.subscriptions.add(
      this.accountingService.expense_records_list_retrieved$.subscribe(
        (response) => {
          this.accountingService.expense_records = [
            ...this.accountingService.expense_records,
          ];
          this.cdr.detectChanges();
        },
      ),
    );
    // expense_records_list_updated$
    this.subscriptions.add(
      this.accountingService.expense_records_list_updated$.subscribe(
        (response) => {
          this.accountingService.expense_records = [
            ...this.accountingService.expense_records,
          ];
          this.cdr.detectChanges();
        },
      ),
    );
  }
  async onGridReady(params: any) {
    this.gridApi = params.api;
    if (!this.agGridService.hasCachedSettingsFor(ComponentNames.expense_table)) {
      await this.agGridService.loadSavedTableSettings(
        ComponentNames.expense_table,
      );
    }
    this.applyCurrentSessionPreferences();
  }
  async ngOnInit(): Promise<void> {
    this.addButtonColumn();
    if (this.accountingService.expense_records.length === 0) {
      this.sharedService.openStatusSpinner('Loading Expense Records');
      await this.accountingService.getExpenseRecords();
      this.sharedService.closeStatusSpinner();
    }
  }
  ngAfterViewInit() {
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }
  async refreshTableDataClick() {
    this.sharedService.openStatusSpinner('Refreshing Expense Records');
    await this.accountingService.getExpenseRecords();
    this.sharedService.closeStatusSpinner();
  }
  addButtonColumn() {
    const hasEditDelete = this.columnDefs.some(
      (column) => column.headerName === 'View / Edit',
    );
    if (!hasEditDelete) {
      this.columnDefs.push({
        // View / Edit
        headerName: 'View / Edit',
        field: 'view_edit',
        resizable: true,
        width: 100,
        minWidth: 100,
        pinned: 'right', // pin to right
        suppressMovable: true, // make not movable
        cellRenderer: 'ViewEditBtns',
        cellRendererParams: {
          editItemClick: this.editExpenseClick.bind(this),
        },
      });
    }
  }
  editExpenseClick(selected_row: { colValues: Expense; rowIndex: number }) {
    const expense = selected_row.colValues;
    this.sharedService.changeRoute('accounting-expense');
    setTimeout(() => {
      this.sharedService.view_edit_item$.next(expense);
    }, 125);
  }
  createNewExpense() {
    this.sharedService.changeRoute('accounting-expense');
  }
  createNewVendorCredit() {
    this.sharedService.changeRoute('accounting-expense');
    setTimeout(() => {
      this.accountingService.create_vendor_credit$.next(true);
    }, 125);
  }
  // preference functions
  storeCurrentSessionFilters() {
    this.agGridService.storeCurrentSessionFilters(
      ComponentNames.expense_table,
      this.gridApi.getFilterModel(),
    );
  }
  storeCurrentSessionColumnConfig() {
    this.agGridService.storeCurrentSessionColumnConfig(
      ComponentNames.expense_table,
      this.gridApi?.getColumnState(),
    );
  }
  applyCurrentSessionPreferences() {
    let component: any = this.agGridService.current_session_filtering.find(
      (component) => component.component_name === ComponentNames.expense_table,
    );
    if (component && !isObjectEmpty(component.filters)) {
      this.gridApi.setFilterModel(component.filters);
    }
    component = this.agGridService.current_session_column_config.find(
      (component) => component.component_name === ComponentNames.expense_table,
    );
    if (component && !isObjectEmpty(component.column_config)) {
      this.gridApi?.applyColumnState({
        state: component.column_config,
        applyOrder: true,
      });
    }
  }
  saveCurrentTablePreferencesClick() {
    const filters = this.gridApi.getFilterModel();
    const column_state = this.gridApi?.getColumnState();
    this.agGridService.saveCurrentTableSettings(
      ComponentNames.expense_table,
      filters,
      column_state,
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }
}

